import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosInstance";
import { useAppSetting } from "../../context/appSettingContext";
import { useAuth } from "../../context/userContext";
import "../../css/fontawesome.css";
import useAlert from "../../hooks/Alert";
import Alert from "../Auth/Alert/Alert";
import AnimatedPage from "../FramerMotion/AnimatedPage";
import PageTitle from "../Misc/PageTitle";
import Navbar from "../Navbar/Navbar";
import EmojiFeedback from "./misc/EmojiFeedback";

function Feedback({ baseURL, clientID }) {
    const [feedbackQuestions, setFeedbackQuestions] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const { authUser } = useAuth();
    const { appSettings } = useAppSetting();
    const { showAlert, setShowAlert, alertStyle, setAlertStyle, alertMessage, setAlertMessage, hideAlert } = useAlert();
    const navigate = useNavigate();

    const validateForm = (formData) => {
        let errors = {};
        const ratingTypes = formData.getAll("rating_type[]");
        const starsSelected = formData.getAll("star_selected[]");
        const emojisSelected = formData.getAll("emoji_selected[]");
    
        // Check if at least one rating is selected for each question
        ratingTypes.forEach((type, index) => {
            const value = type === "Star" ? starsSelected[index] : emojisSelected[index];
            if (value === "" || value === "-1") {
                errors[`question_${index}`] = "Please select a rating";
                
                // Set timeout to remove this error after 2 seconds
                setTimeout(() => {
                    setValidationErrors(prevErrors => {
                        const newErrors = {...prevErrors};
                        delete newErrors[`question_${index}`];
                        return newErrors;
                    });
                }, 3000);
            }
        });
    
        return errors;
    };

    const submitFeedbackForm = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);

        const formData = new FormData(event.target);
        const validationErrors = validateForm(formData);

        if (Object.keys(validationErrors).length > 0) {
            setValidationErrors(validationErrors);
            setAlertStyle({
                background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
            });
            setAlertMessage({
                success: false,
                message: "Please complete all ratings before submitting",
                mode: "Feedback",
            });
            setShowAlert("d-block");
            setIsSubmitting(false);
            return;
        }

        setValidationErrors({});

        const payLoad = {
            DATA: {
                TRAN: [],
            },
        };

        const ratingIDs = formData.getAll("rating_id[]");
        const clientIDs = formData.getAll("client_id[]");
        const ratingNames = formData.getAll("rating_name[]");
        const ratingTypes = formData.getAll("rating_type[]");
        const starsSelected = formData.getAll("star_selected[]");
        const emojisSelected = formData.getAll("emoji_selected[]");
        const otherFeedback = formData.getAll("other_feedback[]");

        for (let index = 0; index < feedbackQuestions.RATINGS.length; index++) {
            const feedback = {
                client_id: clientIDs[index],
                rating_id: ratingIDs[index],
                rating_name: ratingNames[index],
                option_id: ratingTypes[index] === "Star" ? starsSelected[index] : emojisSelected[index],
                customer_code: authUser.CUST_CODE,
                other_feedback: otherFeedback[index],
            };
            payLoad.DATA.TRAN.push(feedback);
        }

        try {
            const response = await axiosInstance.post(`${appSettings.AppBaseURL1}Loy_InsertUpdateFeedbackRatings`, payLoad);

            const res = response.data;

            if (res.STATUS !== "FAIL") {
                setAlertStyle({
                    background: "linear-gradient(to right, #4caf50 0%, #2e7d32 100%)",
                });
                setAlertMessage({
                    success: true,
                    message: "Thank you for your feedback!",
                    mode: "Feedback",
                });
                setShowAlert("d-block");

                Cookies.set("true-value-feedback", true, { expires: 200 });

                setTimeout(() => {
                    navigate("/profile");
                }, 1500);
            } else {
                setAlertStyle({
                    background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
                });
                setAlertMessage({
                    success: false,
                    message: res.MESSAGE || "Failed to submit feedback",
                    mode: "Feedback",
                });
                setShowAlert("d-block");
            }
        } catch (error) {
            console.error("Error submitting feedback:", error);
            setAlertStyle({
                background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
            });
            setAlertMessage({
                success: false,
                message: error.response?.data?.MESSAGE || "Submission failed. Please try again.",
                mode: "Feedback",
            });
            setShowAlert("d-block");
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        getFeedbackQuestions();
    }, []);

    const getFeedbackQuestions = async () => {
        const payLoad = {
            DATA: {
                AUTH_KEY: "TXlDb206TG95QVBJMTIz",
                client_id: clientID,
                publish: "Y",
            },
        };

        try {
            const response = await axiosInstance.post(`${appSettings.AppBaseURL1}Loy_getRatingsAndOptions`, payLoad);

            if (response?.data?.STATUS) {
                setFeedbackQuestions(response.data.DATA);
            } else {
                console.error("Failed to fetch feedback questions:", response.data?.MESSAGE);
                setAlertStyle({
                    background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
                });
                setAlertMessage({
                    success: false,
                    message: "Failed to load feedback questions. Please try again later.",
                    mode: "Feedback",
                });
                setShowAlert("d-block");
            }
        } catch (error) {
            console.error("Error fetching feedback questions:", error);
            setAlertStyle({
                background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
            });
            setAlertMessage({
                success: false,
                message: "Error loading feedback questions. Please check your connection.",
                mode: "Feedback",
            });
            setShowAlert("d-block");
        }
    };

    return (
        <AnimatedPage>
            <Alert showAlert={showAlert} alertStyle={alertStyle} alertMessage={alertMessage} hideAlert={hideAlert} />
            <div className="h-100 bg-wafaa-white-3">
                <div className="position-sticky z-2">
                    <div className={"transition-0-5-bounce"}>
                        <PageTitle hideBackButton={false} name="FEEDBACK" />
                    </div>
                </div>
                <div>
                    <form className="px-3 position-relative overflow-y-scroll pb-5 bg-wafaa-white-3 w-100 mx-auto" onSubmit={submitFeedbackForm}>
                        {feedbackQuestions?.RATINGS !== undefined
                            ? feedbackQuestions?.RATINGS?.map((rating, index) => (
                                  <div key={index}>
                                      <EmojiFeedback rating={rating} ratingOptions={feedbackQuestions?.RATING_OPTIONS} />
                                      {validationErrors[`question_${index}`] && <div className="text-danger small mt-1">{validationErrors[`question_${index}`]}</div>}
                                  </div>
                              ))
                            : ""}

                        {feedbackQuestions?.RATINGS?.length && (
                            <div className="mb-5 w-100">
                                <button className="w-100 mx-auto btn bg-wafaa-purple text-white" type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Submitting...
                                        </>
                                    ) : (
                                        "Share Feedback"
                                    )}
                                </button>
                            </div>
                        )}
                    </form>
                </div>
            </div>
            <Navbar activePage="profile" />
        </AnimatedPage>
    );
}

export default Feedback;
